import { useCallback, useEffect, useRef } from "react";

export interface UnmountSafe {
    /** Returns true while the component is mounted, false after unmount. */
    isMounted: () => boolean;
    /**
     * AbortSignal whose lifetime equals the component's mount cycle. Pass to
     * `fetch(..., { signal })` so unmount cancels in-flight requests.
     * Re-created on remount under React 18 StrictMode's mount/unmount/remount.
     */
    signal: AbortSignal;
}

/**
 * Mount-aware lifecycle helper that survives React 18 StrictMode's
 * mount → cleanup → re-mount double-invoke pattern.
 *
 * The naive pattern — `const ref = useRef(false); useEffect(() => () => ref.current = true)`
 * — leaves `ref.current` stuck at `true` after the StrictMode cleanup, so the
 * remounted component thinks it's permanently unmounted and every guarded
 * `if (ref.current) return` short-circuits forever.
 *
 * This hook does the bidirectional reset: mount sets `mounted = true`;
 * cleanup sets `mounted = false`. The effect runs once per mount cycle,
 * including the StrictMode remount, so the flag tracks reality.
 *
 * The AbortController is the same idea applied to network/async work — abort
 * on unmount so `fetch` aborts, and create a fresh controller on remount so
 * the new mount's requests aren't pre-aborted.
 */
export function useUnmountSafe(): UnmountSafe {
    const mountedRef = useRef(true);
    const controllerRef = useRef<AbortController>(new AbortController());

    // Lazy re-arm: if the controller from a previous mount cycle is aborted,
    // hand out a fresh one. Done in render rather than effect so consumers
    // calling `signal` during their first render get a non-aborted signal.
    if (controllerRef.current.signal.aborted) {
        controllerRef.current = new AbortController();
    }

    useEffect(() => {
        // Bidirectional reset (the line that kills the StrictMode bug).
        mountedRef.current = true;
        // Same for the controller — re-arm if a prior cycle's cleanup aborted it.
        if (controllerRef.current.signal.aborted) {
            controllerRef.current = new AbortController();
        }
        return () => {
            mountedRef.current = false;
            controllerRef.current.abort();
        };
    }, []);

    const isMounted = useCallback(() => mountedRef.current, []);
    return { isMounted, signal: controllerRef.current.signal };
}
